import {
  DetailedHTMLProps,
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';

import { shouldShowEmptyCatalogSearchResult } from '@/lib/entities/catalog/lib/should-show-empty-catalog-search-result';
import { useRentSearchQuery } from '@/lib/entities/rent/api/rent';
import { useSaleSearchQuery } from '@/lib/entities/sale/api';

import { Button } from '@/lib/shared/components/Buttons';
import { modal, FeedBackModal } from '@/lib/shared/components/Modal';
import { P2 } from '@/lib/shared/components/Typography';
import { useDebounce } from '@/lib/shared/hooks/use-debounce';
import useOnClickOutside from '@/lib/shared/hooks/use-on-click-outside';
import { SearchIcon, CloseSign } from '@/lib/shared/icons';

import { clsx } from 'clsx';
import { useTranslation } from 'next-i18next';
import { parseAsString, useQueryState } from 'nuqs';

import styles from './SearchInput.module.scss';

type ISearchInput = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & { type: 'rent' | 'sale' };

export const SearchInput = ({ className, type, ...props }: ISearchInput) => {
  const [isEmptyResult, setIsEmptyResult] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation('index');
  const [query, setQuery] = useQueryState(
    'query',
    parseAsString.withDefault('')
  );
  const [value, setValue] = useState(query || '');
  const [searchValue, setSearchValue] = useState(value);
  const setDebouncedValue = useDebounce(() => setSearchValue(value), 300);
  useEffect(() => {
    setDebouncedValue();
  }, [setDebouncedValue, value]);
  const queryType = type === 'rent' ? useRentSearchQuery : useSaleSearchQuery;
  const { data, isPending, isFetching, isError } = queryType(searchValue);

  const emptyResultText = t('emptyResult');

  const navigate = async (isClear?: boolean) => {
    if (value === emptyResultText) {
      setValue('');
      if (inputRef.current) {
        inputRef.current.blur();
      }
      return setIsEmptyResult(false);
    }

    const searchReady = !isPending && !isFetching && !isError;
    if (
      searchReady &&
      shouldShowEmptyCatalogSearchResult({
        type,
        data,
        hasQuery: value !== '' && !isClear,
      })
    ) {
      setValue(emptyResultText);
      if (inputRef.current) {
        inputRef.current.blur();
      }
      return setIsEmptyResult(true);
    }
    if (!isClear && !value) {
      setQuery(value, { history: 'push', shallow: true });
    } else if (isClear) {
      setQuery(null, { history: 'push', shallow: true });
    } else {
      setQuery(value, { history: 'push', shallow: true });
    }
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const resetQuery = () => {
    setValue(() => '');
    setIsEmptyResult(false);
    navigate(true);
  };

  const openModal = () => {
    modal.open(<FeedBackModal leadType="SALE" eventName={['sale']} />, {
      isFeedBack: true,
    });
    setIsEmptyResult(false);
  };

  useEffect(() => {
    setValue(query === undefined ? '' : decodeURIComponent(query as string));
  }, [query]);

  const emptyResultRef = useRef<HTMLDivElement | null>(null);
  useOnClickOutside(
    emptyResultRef,
    () => isEmptyResult && setIsEmptyResult(false)
  );

  return (
    <form className={styles.search__wrapper} onSubmit={() => navigate()}>
      <input
        value={value}
        ref={inputRef}
        onChange={(e) => {
          if (isEmptyResult) {
            const lastSymbol =
              e.target.value === emptyResultText.slice(0, -1)
                ? ''
                : e.target.value.slice(-1);
            setIsEmptyResult(false);
            return setValue(() => lastSymbol);
          }
          setValue(() => e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            navigate();
          }
        }}
        onFocus={(e) => {
          if (isEmptyResult || e.target.value === emptyResultText) {
            setValue('');
          }
        }}
        onBlur={(e) => {
          if (isEmptyResult && e.target.value.length === 0) {
            setValue(emptyResultText);
          }
        }}
        name="query"
        className={clsx(styles.search__input, className)}
        type="text"
        {...props}
      />
      <div className={styles.search__icons}>
        {!isEmptyResult && value !== '' && (
          <SearchIcon
            className={styles.search__icon}
            onClick={() => navigate()}
          />
        )}
        {(value || (isEmptyResult && value !== emptyResultText)) && (
          <button
            className={styles.search__reset}
            type="button"
            onClick={() => resetQuery()}
          >
            <CloseSign />
          </button>
        )}
      </div>
      {isEmptyResult && (
        <div className={styles.search__popup} ref={emptyResultRef}>
          <P2>{t('callManager')}</P2>
          <Button
            name="call manager"
            as="button"
            label={t('form.buttons.requestACall')}
            theme={'dark'}
            onClick={openModal}
            size={'md'}
            type="button"
          />
        </div>
      )}
    </form>
  );
};
